import { useUIStore } from '@/state/uiStore';
import { useBrainStore } from '@/state/brainStore';
import { useNavigate } from 'react-router-dom';
import { answerInboundCall, declineInboundCall } from '@/domain/callFlow';
import { PhoneIncoming, PhoneOff, ArrowUpRight } from 'lucide-react';

export default function IncomingCallOverlay() {
  const navigate = useNavigate();
  const calls = useUIStore((s) => s.incomingCalls).filter((c) => c.status === 'ringing');
  const customers = useBrainStore((s) => s.customers);
  const studios = useBrainStore((s) => s.studios);
  const setStatus = useUIStore((s) => s.setIncomingCallStatus);

  if (calls.length === 0) return null;
  return (
    <div className="fixed top-20 right-6 z-[60] flex flex-col gap-3">
      {calls.map((c) => {
        const cust = customers[c.customerId];
        const studio = studios[c.studioId];
        const customerHref = `/customer/${c.customerId}`;
        return (
          <div
            key={c.id}
            data-testid={`incoming-call-${c.id}`}
            className="bg-ink-deep border-l-4 border-gold-bright rounded-lg shadow-gold-glow px-5 py-4 w-[420px] animate-ring-pulse"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="label-small-caps text-gold-bright">
                  Inbound to {studio?.name ?? c.studioId.replace('-', ' ')}
                </p>
                <p className="heading-display text-2xl text-cream mt-1 truncate">
                  {cust ? `${cust.firstName} ${cust.lastName}` : 'Unknown caller'}
                </p>
                <p className="font-mono text-xs text-cream-soft mt-2">
                  {cust?.phone ?? ''} · {studio?.brandedNumber ?? ''}
                </p>
              </div>
              <button
                type="button"
                onClick={() => navigate(customerHref)}
                className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-inter text-cream-soft hover:text-gold-bright transition"
                data-testid={`open-customer-${c.id}`}
                title="Open customer record without answering"
              >
                Customer record <ArrowUpRight size={11} />
              </button>
            </div>
            {cust?.designIntent ? (
              <p className="font-serif text-cream-soft text-sm mt-2">
                Likely about: {cust.designIntent.type}, {cust.designIntent.placement},{' '}
                {cust.designIntent.size}.
              </p>
            ) : null}
            <div className="flex gap-2 mt-4">
              <button
                onClick={() => {
                  setStatus(c.id, 'answered');
                  answerInboundCall(c.id);
                  navigate(customerHref);
                }}
                className="btn-primary text-xs"
                data-testid={`answer-${c.id}`}
              >
                <PhoneIncoming size={12} /> Answer
              </button>
              <button
                onClick={() => {
                  setStatus(c.id, 'declined');
                  declineInboundCall(c.id);
                }}
                className="btn-ghost text-xs"
                data-testid={`decline-${c.id}`}
              >
                <PhoneOff size={12} /> Decline
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
